import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { MagnifyingGlass, Plus, Download } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import NoDataFallback from '../common/NoDataFallback';
import {
  getInvoicesApi,
  generateInvoiceApi,
  downloadInvoicePdfApi,
} from '../../utils/apis';
import { IInvoice } from '../../schemas/apis/interface';
import { format } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const statusColors = {
  paid: { bg: '#E6F4EA', color: '#2E7D32', label: 'Paid' },
  sent: { bg: '#E3F2FD', color: '#1E88E5', label: 'Sent' },
  draft: { bg: '#F5F5F5', color: '#757575', label: 'Draft' },
  overdue: { bg: '#FFEBEE', color: '#C62828', label: 'Overdue' },
  generated: { bg: '#FFF3E0', color: '#F57C00', label: 'Generated' },
  cancelled: { bg: '#FAFAFA', color: '#616161', label: 'Cancelled' },
};

const Invoices = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // Generate Invoice Modal state
  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [generating, setGenerating] = useState(false);
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();

  const {
    data: invoicesData,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: [
      'invoices',
      paginationModel.page,
      paginationModel.pageSize,
      statusFilter,
    ],
    queryFn: async () => {
      const params: any = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await getInvoicesApi(params);
      if (!response.data.success) {
        throw new Error('Failed to fetch invoices');
      }
      return response.data.data as {
        invoices: IInvoice[];
        pagination: { total: number };
      };
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const invoices = invoicesData?.invoices;
  const totalCount = invoicesData?.pagination.total ?? 0;
  const error = (() => {
    if (!queryError) return null;
    if (typeof queryError === 'object' && queryError !== null) {
      const maybeAxiosMessage = (
        queryError as { response?: { data?: { message?: unknown } } }
      ).response?.data?.message;
      if (typeof maybeAxiosMessage === 'string' && maybeAxiosMessage.trim()) {
        return maybeAxiosMessage;
      }
    }
    return queryError instanceof Error && queryError.message
      ? queryError.message
      : 'Failed to fetch invoices';
  })();

  // Generate Invoice handler
  const handleGenerateInvoice = async () => {
    if (!startDate || !endDate) {
      showAlert('Please select both start and end dates', 'error');
      return;
    }

    if (startDate > endDate) {
      showAlert('Start date cannot be after end date', 'error');
      return;
    }

    if (endDate > new Date()) {
      showAlert('End date cannot be in the future', 'error');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        periodType: 'custom' as const,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
      };

      const response = await generateInvoiceApi(payload);

      if (response.data.success) {
        showAlert('Invoice generated successfully!', 'success');
        setOpenGenerateModal(false);
        setStartDate(null);
        setEndDate(null);
        // Refresh the invoices list
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || 'Failed to generate invoice';
      showAlert(errorMessage, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | null
  >(null);

  const { mutate: downloadInvoiceMutation } = useMutation({
    mutationFn: ({ invoiceId }: { invoiceId: string; invoiceNumber: string }) =>
      downloadInvoicePdfApi(invoiceId),
    onMutate: ({ invoiceId }) => {
      setDownloadingInvoiceId(invoiceId);
    },
    onSuccess: (response, { invoiceNumber }) => {
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showAlert('Invoice downloaded successfully', 'success');
      setDownloadingInvoiceId(null);
    },
    onError: (err: any) => {
      const errorMessage =
        err.response?.data?.message || 'Failed to download invoice';
      showAlert(errorMessage, 'error');
      setDownloadingInvoiceId(null);
    },
  });

  // Download invoice PDF handler
  const handleDownloadInvoice = (invoiceId: string, invoiceNumber: string) => {
    downloadInvoiceMutation({ invoiceId, invoiceNumber });
  };

  // Filter invoices based on search
  const filteredInvoices = useMemo(() => {
    const list: IInvoice[] = invoices ?? [];
    if (!searchQuery) return list;

    return list.filter(invoice => {
      const matchesSearch =
        invoice.invoiceNumber
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        `${format(new Date(invoice.period.startDate), 'dd MMM yyyy')} - ${format(new Date(invoice.period.endDate), 'dd MMM yyyy')}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [invoices, searchQuery]);

  const columns: GridColDef[] = [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice',
      flex: 1,
      minWidth: 180,
      headerClassName: 'custom-header',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: '#2B2D3F', fontSize: '14px' }}
        >
          {params.row.invoiceNumber}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 0.8,
      minWidth: 120,
      headerClassName: 'custom-header',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: '#6B6F80', fontSize: '14px' }}>
          {format(new Date(params.row.createdAt), 'dd MMM yyyy')}
        </Typography>
      ),
    },
    {
      field: 'period',
      headerName: 'Period',
      flex: 1.2,
      minWidth: 200,
      headerClassName: 'custom-header',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: '#6B6F80', fontSize: '14px' }}>
          {`${format(new Date(params.row.period.startDate), 'dd MMM yyyy')} - ${format(new Date(params.row.period.endDate), 'dd MMM yyyy')}`}
        </Typography>
      ),
    },
    {
      field: 'total',
      headerName: 'Amount',
      flex: 0.8,
      minWidth: 120,
      headerClassName: 'custom-header',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: '#2B2D3F', fontSize: '14px' }}
        >
          ${params.row.total.toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'totalMinutes',
      headerName: 'Minutes',
      flex: 0.7,
      minWidth: 100,
      headerClassName: 'custom-header',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: '#6B6F80', fontSize: '14px' }}>
          {params.row.usage.totalMinutes} min
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row.status as keyof typeof statusColors;
        const statusConfig = statusColors[status];
        return (
          <Box
            sx={{
              backgroundColor: statusConfig.bg,
              color: statusConfig.color,
              width: 'auto',
              minWidth: 78,
              maxWidth: 130,
              height: 24,
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {status === 'paid' && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusConfig.color,
                  mr: 0.5,
                }}
              />
            )}
            {statusConfig.label}
          </Box>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: '#6B6F80', fontSize: '14px' }}>
          {params.row.dueDate}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const isDownloading = downloadingInvoiceId === params.row._id;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="Download Invoice">
              <IconButton
                size="small"
                onClick={() =>
                  handleDownloadInvoice(
                    params.row._id,
                    params.row.invoiceNumber
                  )
                }
                disabled={isDownloading}
                sx={{
                  '&:hover': {
                    bgcolor: '#F8F9FC',
                  },
                }}
              >
                {isDownloading ? (
                  <CircularProgress size={18} />
                ) : (
                  <Download size={18} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontSize: '20px', fontWeight: 600, color: '#2B2D3F' }}
            >
              Invoices
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '14px' }}
            >
              Generate and manage your billing invoices
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Plus size={16} weight="bold" />}
            onClick={() => setOpenGenerateModal(true)}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              height: '34px',
              bgcolor: '#5A4CE6',
              color: '#FFFFFF',
              boxShadow: 'none',
              fontSize: '14px',
              fontWeight: 500,
              display: { xs: 'none', sm: 'flex' },
              '&:hover': {
                bgcolor: '#4A3CD6',
                boxShadow: 'none',
              },
            }}
          >
            Generate Invoice
          </Button>
        </Box>

        {/* Search and Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <TextField
            placeholder="Search by invoice number..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlass size={20} color="#6B6F80" />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF',
                '& fieldset': {
                  borderWidth: '1px',
                  borderColor: '#DCE0E7',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '14px',
                color: '#302B3F',
              },
            }}
          />
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 200 },
              height: '40px',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderWidth: '1px',
                borderColor: '#DCE0E7',
              },
              '& .MuiSelect-select': {
                padding: '8px 12px',
                fontSize: '14px',
                color: '#302B3F',
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="generated">Generated</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
          <Button
            variant="contained"
            startIcon={<Plus size={16} weight="bold" />}
            onClick={() => console.log('Generate invoice')}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              height: '34px',
              bgcolor: '#5A4CE6',
              color: '#FFFFFF',
              boxShadow: 'none',
              fontSize: '14px',
              fontWeight: 500,
              display: { xs: 'flex', sm: 'none' },
              '&:hover': {
                bgcolor: '#4A3CD6',
                boxShadow: 'none',
              },
            }}
          >
            Generate Invoice
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Box
        sx={{
          height: 600,
          backgroundColor: '#FFF',
          borderRadius: '8px',
          border: '1px solid #DCE0E7',
        }}
      >
        <DataGrid
          rows={filteredInvoices}
          columns={columns}
          getRowId={row => row._id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={totalCount}
          pageSizeOptions={[5, 10, 25]}
          loading={loading}
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: () => (
              <NoDataFallback
                message="No Invoices Found"
                description={
                  searchQuery || statusFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Generate your first invoice to get started'
                }
              />
            ),
          }}
          sx={{
            borderRadius: '8px',
            color: '#6B6F80',
            border: 'none',
            '& .MuiDataGrid-root': {
              borderRadius: '8px',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-main': {
              borderRadius: '8px',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #F5F5F5',
            },
            '& .MuiDataGrid-columnHeader': {
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
            },
            '& .custom-header': {
              bgcolor: '#F8F9FC',
              color: '#2B2D3F',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 16px',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#2B2D3F',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#F8F9FC',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'transparent !important',
              border: 'none !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: '#E7F0FF !important',
            },
            '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #DCE0E7',
            },
          }}
        />
      </Box>

      {/* Generate Invoice Modal */}
      <Dialog
        open={openGenerateModal}
        onClose={() => !generating && setOpenGenerateModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#2B2D3F',
            pb: 1,
          }}
        >
          Generate Invoice
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, fontSize: '14px' }}
          >
            Select the date range for which you want to generate an invoice. The
            invoice will include all call data within this period.
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={newValue => setStartDate(newValue)}
                views={['year', 'month', 'day']}
                format="dd-MM-yyyy"
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& input': {
                        fontSize: '14px',
                        color: '#2B2D3F',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                  },
                }}
              />

              <DatePicker
                label="End Date"
                value={endDate}
                onChange={newValue => setEndDate(newValue)}
                views={['year', 'month', 'day']}
                format="dd-MM-yyyy"
                minDate={startDate || undefined}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& input': {
                        fontSize: '14px',
                        color: '#2B2D3F',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          {generating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Generating invoice...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenGenerateModal(false);
              setStartDate(null);
              setEndDate(null);
            }}
            disabled={generating}
            sx={{
              textTransform: 'none',
              color: '#6B6F80',
              '&:hover': {
                bgcolor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateInvoice}
            variant="contained"
            disabled={generating || !startDate || !endDate}
            sx={{
              textTransform: 'none',
              bgcolor: '#5A4CE6',
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#4A3CD6',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: '#E0E0E0',
                color: '#9E9E9E',
              },
            }}
          >
            {generating ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Message */}
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default Invoices;
